import { Component, OnInit, OnDestroy } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { combineLatest, Subject } from 'rxjs'
import { first, map, takeUntil } from 'rxjs/operators'
import { CookieService } from 'ngx-cookie'
import * as _ from 'lodash'
import * as moment from 'moment-timezone'

import { Persona } from '../models/persona'
import { Membership } from '../models/membership'
import { SeMembershipCard, SeAvatarGroupAvatar } from '../models/membership-card-types'
import { Organization } from '../models/organization'
import { Mapper } from '../services/mapper.service'
import { environment } from '../../environments/environment'
import { MembershipService } from '../services/membership.service'
import { OrganizationService } from '../services/organization.service'
import { PersonaService } from '../services/persona.service'
import { DownloadParams, CardIdParams } from '../models/download'
import { MembershipEffectiveType } from 'app/models'
import { SeAnalyticsService } from '../services/se-analytics.service'

@Component({
  standalone: false,
  selector: 'app-post-checkout',
  templateUrl: './post-checkout.component.html',
  styleUrls: ['./post-checkout.scss']
})
export class PostCheckoutComponent implements OnInit, OnDestroy {

  public persona: Persona
  public error: string
  public effectiveAt: Date
  public expiresAt: Date
  public membership: Membership
  public seMembershipCard: SeMembershipCard = new SeMembershipCard({ })
  public memberships: Membership[]
  public organization: Organization
  public affiliateOrganization: Organization
  public parentOrganization: Organization
  public loaded = false
  public buttonValue: string
  public buttonUrl: string
  public surveyUrl: string
  public mySeUrl: string
  public membershipStatus: string
  public iosDownloadParams: DownloadParams
  public androidDownloadParams: DownloadParams
  private saleGroupOrSaleNumber: string
  private saleNumbers: string[]
  private membershipId: string
  private unsubscribe: Subject<any> = new Subject()
  private cardOganization: SeAvatarGroupAvatar
  private cardParentOrganization: SeAvatarGroupAvatar

  constructor(
    public acRoute: ActivatedRoute,
    private membershipService: MembershipService,
    private personaService: PersonaService,
    private organizationService: OrganizationService,
    private cookieService: CookieService,
    private seAnalyticsService: SeAnalyticsService
  ) { }

  public ngOnInit(): void {
    this.initRouteListener()
    this.initOrganization()
    this.initPersona()
    this.seAnalyticsService.pageView({
      depth1: 'Memberships',
      depth2: 'PostCheckout',
      depth3: 'Confirmation'
    })
  }

  /**
   * requestedOrganization - used to displayed membership information for
   * a membership in requested state; affiliate or membership org
   */
  public get requestedOrganization(): Organization {
    return this.affiliateOrganization || this.organization
  }

  public isLifetime(membership: Membership) {
    return membership.membership_definition.effective_type === MembershipEffectiveType.Lifetime
  }

  private initRouteListener(): void {
    combineLatest([this.acRoute.params, this.acRoute.queryParams])
      .pipe(takeUntil(this.unsubscribe))
      .pipe(map(results => ({ params: results[0], queryParams: results[1] })))
      .subscribe(({ params, queryParams }) => {
        this.saleGroupOrSaleNumber = params.saleNumber
        if (queryParams.saleNumbers) this.saleNumbers = (queryParams.saleNumbers as string).split(',')
        else if (params.saleNumber) this.saleNumbers = [params.saleNumber]
        this.membershipId = params.membershipId
        this.initMembership()
      })
  }

  private initMembership(): void {
    if (this.saleNumbers) this.handleCheckout()
    else if (this.membershipId) this.handleAssignment()
  }

  private initRedirectButton(): void {
    if (!this.surveyUrl && this.membership.isRequested) {
      this.buttonValue = 'Continue to MySportsEngine'
      this.buttonUrl = this.mySeUrl
    }
    else if (!this.surveyUrl && !this.membership.isRequested) {
      this.buttonValue = 'Continue to MySportsEngine'
      this.buttonUrl = this.mySeUrl
    }
    else if (this.surveyUrl) {
      this.buttonValue = 'Continue to Registration'
      this.buttonUrl = this.surveyUrl
    }
  }

  public redirect(): void {
    window.location.href = this.buttonUrl
  }

  private handleCheckout(): void {
    combineLatest(this.saleNumbers.map((saleNumber) => this.membershipService.verifyMembershipFromSaleNumber(saleNumber)))
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((responses: Array<Partial<{ result: Membership[]}>>) => {
        this.memberships = responses.map((response): Membership => (
          // CAN DO [0] SINCE WE ONLY ALLOW ONE MEMBERSHIP TO BE BOUGHT
          Mapper.asArray<Membership>(response.result, Membership)[0]
        ))
        this.setupMemberships()
      })
  }

  private handleAssignment(): void {
    this.membershipService.find(this.membershipId)
      .subscribe(mem => {
        this.memberships = [mem]
        this.setupMemberships()
      })
  }

  private setupMemberships(): void {
    if (!this.memberships || !this.memberships.length) return
    this.membership = this.getCurrentMembership()
    this.membershipStatus = this.membership.membershipStatus === 'valid' ? 'paid' : this.membership.membershipStatus
    this.seMembershipCard.member.number = this.membership.number.number
    this.seMembershipCard.memberships[0].name = this.membership.membership_definition?.name
    this.seMembershipCard.memberships[0].status = this.membershipStatus

    this.mySeUrl = environment.mySeUrl + '/profile/' + this.membership.persona_id
    if (this.memberships.length === 1) {
      this.mySeUrl += '/org-memberships/' + this.membership.membership_definition.boss_organization_id.toString()
    }
    this.iosDownloadParams = new CardIdParams(this.membership.card_id, 'ios')
    this.androidDownloadParams = new CardIdParams(this.membership.card_id, 'android')
    this.personaService.getPersona(this.membership.persona_id)
    this.organizationService.findCurrent(this.membership.membership_definition.boss_organization_id.toString())
    this.getAffiliateOrganization(this.membership.affiliation_organization_id)
    this.getParentOrganization()
    this.initRedirectButton()
    this.checkLoaded()
  }

  private getCurrentMembership(): Membership {
    if (this.memberships.length === 1) return this.memberships[0]
    return this.memberships.find((membership: Membership) =>
      membership.membership_definition.hasParentMembershipDefinitions
    ) || this.memberships[0] // if multiple memberships, only 1 will have parent mem defs
  }

  private getParentOrganization(): void {
    if (this.memberships.length === 1) return
    const parentMembership = this.memberships.find((membership: Membership) =>
      !membership.membership_definition.hasParentMembershipDefinitions
    )
    this.organizationService.find(parentMembership.membership_definition.boss_organization_id.toString())
      .pipe(first())
      .subscribe((data: any) => {
        this.parentOrganization = Mapper.as<Organization>(data.result, Organization)
        this.cardParentOrganization = new SeAvatarGroupAvatar({
          identity: this.parentOrganization.name, imgUrl: this.parentOrganization.logo?.small_logo_url })
        this.updateCardOrganizations()
        this.checkLoaded()
      })
  }

  public ngOnDestroy() {
    this.unsubscribe.next()
    this.unsubscribe.complete()
  }

  private getAffiliateOrganization(id: number) {
    if (!id)  return
    this.organizationService.find(id.toString())
      .pipe(first())
      .subscribe((data: any) => {
        this.affiliateOrganization = Mapper.as<Organization>(data.result, Organization)
        this.checkLoaded()
      })
  }

  private initDates() {
    this.seMembershipCard.memberships[0].effectiveAt = moment(this.membership.effective_at)
      .tz(this.organization.timezoneOrDefault).toISOString()
    this.seMembershipCard.memberships[0].expiresAt = moment(this.membership.expires_at)
      .tz(this.organization.timezoneOrDefault).toISOString()
  }

  private initPersona() {
    this.personaService.current.subscribe(
      (persona) => {
        this.persona = persona
        this.seMembershipCard.member.image = persona.profileImage('crop')
        this.seMembershipCard.member.name = persona.fullName
        this.checkLoaded()
      }
    )
  }

  private initOrganization() {
    this.organizationService.current.subscribe(
      (org) => {
        this.organization = org
        this.cardOganization = new SeAvatarGroupAvatar({ identity: org.name, imgUrl: org.logo?.small_logo_url })
        this.updateCardOrganizations()
        this.checkLoaded()
      }
    )
  }

  public redirectToQuizzard(): void {
    window.location.href = `/org/${this.organization.slug}/affiliation/${this.affiliateOrganization.slug}/member-info/${this.persona.id}`
  }

  private updateCardOrganizations() {
    const orgs = []
    if (this.cardParentOrganization) orgs.push(this.cardParentOrganization)
    if (this.cardOganization) orgs.push(this.cardOganization)
    this.seMembershipCard.organizations = orgs
  }

  private checkLoaded() {
    const requests = [this.organization, this.persona, this.membership]
    if (this.membership && this.membership.affiliation_organization_id) {
      requests.push(this.affiliateOrganization)
    }
    if (this.memberships && this.memberships.length > 1) requests.push(this.parentOrganization)
    if (_.compact(_.map(requests, 'id')).length === requests.length) {
      this.checkSurvey()
      this.initDates()
      this.loaded = true
    }
  }

  private checkSurvey() {
    const cookieName: string = 'survey_url_' + this.saleGroupOrSaleNumber
    this.surveyUrl = this.cookieService.get(cookieName)
    if (this.surveyUrl) this.surveyUrl += '?membership_id=' + this.membership.id
    this.cookieService.remove(cookieName)
  }
}
